<template>
  <div class="asc-report-grid">
    <asc-ag-grid-filter
      :key="`asc-report-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAlertIconCellRenderer,
  AscAgGridAutocompletePersistentFloatFilter,
  AscAgGridDatePersistentFloatFilter,
  AscAgGridNumberPersistentFloatFliter,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getFormattedDateTime } from '@/utilities/dates'
import AppFeatureFlags from '@/utilities/appFeatureFlags'
import {
  stringTextMatcher,
  dateStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscReportGrid',

  components: {
    AscAgGridFilter,
    AscAlertIconCellRenderer,
    AscAgGridAutocompletePersistentFloatFilter,
    AscAgGridDatePersistentFloatFilter,
    AscAgGridNumberPersistentFloatFliter,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    onOpenReportSummary: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        getContextMenuItems: this.getContextMenuItems,
        localeText: { noRowsToShow: this.getNoRowsToShowText() },
        rowSelection: 'single',
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onSortChanged: this.handleGridSortChange, // This is required id for persistent filters to update on change
        onFilterChanged: this.handleGridFilterChange, // This is required id for persistent filters to update on change
        onCellClicked: this.handleCellClicked,
      }),
      persistentGridId: 'asc-report-grid', // This is required id for persistent filters
    }
  },

  computed: {
    mappedValues() {
      const updatedItems = this.values.map(item => {
        return {
          ...item,
          statusAlertCountValue: this.getStatusAlertCountValue(item),
        }
      })
      return updatedItems
    },
    agGridRowData() {
      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: 'Alert Type',
          field: 'statusAlertCountValue',
          cellRenderer: 'AscAlertIconCellRenderer',
          hide: AppFeatureFlags.getReportListAlertColumn() === false,
        },
        {
          headerName: 'Alerts',
          field: 'alertCount',
          sort: 'desc',
          filter: 'agNumberColumnFilter',
          floatingFilterComponent: 'AscAgGridNumberPersistentFloatFliter',
          hide: AppFeatureFlags.getReportListAlertColumn() === false,
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            disabled: !this.getUniqueItems('alertCount').length || false,
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Platform',
          field: 'modelName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('modelName'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Report Type',
          field: 'reportType',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('reportType'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'System',
          field: 'systemName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('systemName'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Tail',
          field: 'tailNumber',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompletePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            ids: [],
            items: this.getUniqueItems('tailNumber'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Report Date',
          field: 'reportDate',
          width: 155,
          minWidth: 155,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDatePersistentFloatFilter',
          floatingFilterComponentParams: {
            persistentGridId: this.persistentGridId,
            suppressFilterButton: true,
            items: this.values,
            dateFloatFilterConfig: {
              left: true,
              transitionOrigin: 'top right',
            },
          },
          valueFormatter: data => getFormattedDateTime(data.value),
          comparator: this.sortDate,
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    handleCellClicked(event) {
      if (event) {
        this.onOpen(event.data)
      }
    },

    getNoRowsToShowText() {
      return this.isGridDataLoaded
        ? 'No Data Displayed'
        : 'Choose filter entry options to see reports.'
    },

    getContextMenuItems(params) {
      const reportId = params.node.data.reportId
      const tailNumber = params.node.data.tailNumber
      const url = `/reportDetails/reportid/${reportId}/tail/${tailNumber}`
      const _that = this
      const result = [
        {
          name: 'Open Link in New Tab',
          action: function () {
            window.open(url, '_blank', 'noopener', 'noreferrer')
          },
        },
        {
          name: 'Open Reports Summary',
          action: function () {
            if (_that.onOpenReportSummary) {
              _that.onOpenReportSummary()
            }
          },
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'export',
      ]

      return result
    },
  },
}
</script>
